<template>
  <div class="row">
    <div class="col-md-12">
      <div class="card vx-card">
        <div class="card-header bg-white">
          <h4 class="card-title d-flex justify-content-between align-items-center flex-wrap">
            <div>
              Monitoreo
              <small class="text-muted d-block mt-1" style="font-size: 12px; font-weight: normal;">
                Logs de Laravel en tiempo casi real
              </small>
            </div>
            <div class="d-flex align-items-center flex-wrap mt-2 mt-md-0">
              <span
                class="badge mr-3"
                :class="autoRefresh ? 'badge-success' : 'badge-secondary'"
              >
                {{ autoRefresh ? "EN VIVO" : "PAUSADO" }}
              </span>
              <vs-button
                v-if="!autoRefresh"
                type="gradient"
                size="small"
                class="mr-2"
                @click="startAutoRefresh"
              >
                <i class="fa fa-play"></i> Iniciar
              </vs-button>
              <vs-button
                v-else
                color="warning"
                type="filled"
                size="small"
                class="mr-2"
                @click="stopAutoRefresh"
              >
                <i class="fa fa-pause"></i> Pausar
              </vs-button>
              <vs-button type="border" size="small" :disabled="loading" @click="fetchLogs">
                <i class="fa fa-refresh"></i> Actualizar
              </vs-button>
            </div>
          </h4>
        </div>

        <div class="card-body" v-loading="loading && !lines.length">
          <div class="row mb-3">
            <div class="col-md-3 mb-2">
              <label>Archivo</label>
              <el-select
                v-model="selectedFile"
                filterable
                placeholder="Archivo de log"
                style="width: 100%"
                @change="onFiltersChange"
              >
                <el-option
                  v-for="file in files"
                  :key="file.name"
                  :label="`${file.name} (${file.size_human})`"
                  :value="file.name"
                ></el-option>
              </el-select>
            </div>
            <div class="col-md-2 mb-2">
              <label>Nivel</label>
              <el-select
                v-model="level"
                clearable
                placeholder="Todos"
                style="width: 100%"
                @change="onFiltersChange"
              >
                <el-option label="Todos" value=""></el-option>
                <el-option label="ERROR" value="error"></el-option>
                <el-option label="WARNING" value="warning"></el-option>
                <el-option label="INFO" value="info"></el-option>
                <el-option label="DEBUG" value="debug"></el-option>
                <el-option label="CRITICAL" value="critical"></el-option>
              </el-select>
            </div>
            <div class="col-md-2 mb-2">
              <label>Líneas</label>
              <el-select
                v-model="linesCount"
                style="width: 100%"
                @change="onFiltersChange"
              >
                <el-option :value="100" label="100"></el-option>
                <el-option :value="200" label="200"></el-option>
                <el-option :value="500" label="500"></el-option>
                <el-option :value="1000" label="1000"></el-option>
              </el-select>
            </div>
            <div class="col-md-3 mb-2">
              <label>Buscar</label>
              <el-input
                v-model="search"
                clearable
                placeholder="Texto en el log..."
                @keyup.enter.native="onFiltersChange"
                @clear="onFiltersChange"
              >
                <el-button
                  slot="append"
                  icon="el-icon-search"
                  @click="onFiltersChange"
                ></el-button>
              </el-input>
            </div>
            <div class="col-md-2 mb-2">
              <label>Intervalo</label>
              <el-select v-model="refreshInterval" style="width: 100%" @change="restartAutoRefresh">
                <el-option :value="2000" label="2 s"></el-option>
                <el-option :value="3000" label="3 s"></el-option>
                <el-option :value="5000" label="5 s"></el-option>
                <el-option :value="10000" label="10 s"></el-option>
              </el-select>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
            <small class="text-muted">
              <span v-if="meta.file">
                {{ meta.file }} · {{ meta.file_size_human }} ·
                modificado {{ meta.modified_at }}
              </span>
              <span v-if="meta.fetched_at"> · actualizado {{ meta.fetched_at }}</span>
            </small>
            <small class="text-muted">{{ lines.length }} entradas</small>
          </div>

          <div ref="logPanel" class="log-panel" @scroll="onScroll">
            <div v-if="!lines.length && !loading" class="log-empty">
              No hay entradas para mostrar con los filtros actuales.
            </div>
            <div
              v-for="(entry, index) in lines"
              :key="index"
              class="log-entry"
              :class="'level-' + (entry.level || 'INFO').toLowerCase()"
            >
              <span class="log-meta" v-if="entry.datetime">
                <span class="log-time">{{ entry.datetime }}</span>
                <span class="log-level">{{ entry.level }}</span>
              </span>
              <pre class="log-message">{{ entry.message }}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import monitoringService from "@/services/monitoring.service";

export default {
  name: "monitoring",
  data() {
    return {
      loading: false,
      autoRefresh: true,
      refreshInterval: 3000,
      timer: null,
      files: [],
      selectedFile: "",
      level: "",
      search: "",
      linesCount: 200,
      lines: [],
      meta: {},
      stickToBottom: true
    };
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    async init() {
      await this.loadFiles();
      await this.fetchLogs();
      this.startAutoRefresh();
    },
    async loadFiles() {
      try {
        const res = await monitoringService.getLogFiles();
        if (res.data && res.data.success) {
          this.files = res.data.files || [];
          if (!this.selectedFile && this.files.length) {
            this.selectedFile = this.files[0].name;
          }
        }
      } catch (err) {
        this.$message({
          showClose: true,
          message: this.errorMessage(err, "No se pudieron cargar los archivos de log"),
          type: "error"
        });
      }
    },
    async fetchLogs() {
      if (this.loading) return;
      this.loading = true;
      try {
        const res = await monitoringService.getLogs({
          file: this.selectedFile,
          lines: this.linesCount,
          level: this.level,
          search: this.search
        });
        if (res.data && res.data.success) {
          this.lines = res.data.lines || [];
          this.meta = {
            file: res.data.file,
            file_size_human: res.data.file_size_human,
            modified_at: res.data.modified_at,
            fetched_at: res.data.fetched_at
          };
          if (!this.selectedFile && res.data.file) {
            this.selectedFile = res.data.file;
          }
          this.$nextTick(() => {
            if (this.stickToBottom) {
              this.scrollToBottom();
            }
          });
        } else {
          this.$message({
            showClose: true,
            message: (res.data && res.data.message) || "Error al leer logs",
            type: "error"
          });
        }
      } catch (err) {
        this.stopAutoRefresh();
        this.$message({
          showClose: true,
          message: this.errorMessage(err, "Error al obtener logs"),
          type: "error"
        });
      } finally {
        this.loading = false;
      }
    },
    onFiltersChange() {
      this.stickToBottom = true;
      this.fetchLogs();
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.autoRefresh = true;
      this.timer = setInterval(() => {
        this.fetchLogs();
      }, this.refreshInterval);
    },
    stopAutoRefresh() {
      this.autoRefresh = false;
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    restartAutoRefresh() {
      if (this.autoRefresh) {
        this.startAutoRefresh();
      }
    },
    scrollToBottom() {
      const panel = this.$refs.logPanel;
      if (panel) {
        panel.scrollTop = panel.scrollHeight;
      }
    },
    onScroll() {
      const panel = this.$refs.logPanel;
      if (!panel) return;
      const threshold = 40;
      this.stickToBottom =
        panel.scrollHeight - panel.scrollTop - panel.clientHeight < threshold;
    },
    errorMessage(err, fallback) {
      if (err && err.response && err.response.data && err.response.data.message) {
        return err.response.data.message;
      }
      if (typeof err === "string") return err;
      return fallback;
    }
  }
};
</script>

<style scoped>
.log-panel {
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 6px;
  height: 65vh;
  min-height: 360px;
  overflow-y: auto;
  padding: 12px 14px;
  font-family: Consolas, "Courier New", monospace;
  font-size: 12px;
  line-height: 1.45;
}

.log-empty {
  color: #888;
  padding: 24px;
  text-align: center;
}

.log-entry {
  border-left: 3px solid #555;
  padding: 4px 0 4px 10px;
  margin-bottom: 6px;
}

.log-entry.level-error,
.log-entry.level-critical,
.log-entry.level-alert,
.log-entry.level-emergency {
  border-left-color: #f56565;
}

.log-entry.level-warning {
  border-left-color: #ecc94b;
}

.log-entry.level-info,
.log-entry.level-notice {
  border-left-color: #63b3ed;
}

.log-entry.level-debug {
  border-left-color: #a0aec0;
}

.log-meta {
  display: block;
  margin-bottom: 2px;
}

.log-time {
  color: #9cdcfe;
  margin-right: 8px;
}

.log-level {
  font-weight: 700;
  color: #ce9178;
}

.log-entry.level-error .log-level,
.log-entry.level-critical .log-level {
  color: #f56565;
}

.log-entry.level-warning .log-level {
  color: #ecc94b;
}

.log-message {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: #d4d4d4;
  font-family: inherit;
  font-size: inherit;
}
</style>
